import { useState, useRef, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { collection, query, where, orderBy, onSnapshot, addDoc, serverTimestamp, updateDoc, doc, getDoc, deleteDoc, getDocs } from 'firebase/firestore'
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage'
import { db, storage } from '../../config/firebase'
import { useAuthStore } from '../../store/authStore'
import { Send, Paperclip, MessageSquare, Search, MessageCircle, Image as Loader2, Briefcase, Trash2, Smile, Filter, X, Check } from 'lucide-react'
import toast from 'react-hot-toast'
import { ChatChannel, ChatMessage, User as UserType } from '../../types'
import { notificationService } from '../../services/notificationService'
import EmojiPicker from 'emoji-picker-react'

export default function ChatTab() {
    const { user, userData, currentOrg, userRole } = useAuthStore()
    const location = useLocation()

    // Data
    const [channels, setChannels] = useState<ChatChannel[]>([])
    const [activeChannelId, setActiveChannelId] = useState<string | null>(null)
    const [messages, setMessages] = useState<ChatMessage[]>([])
    const [departments, setDepartments] = useState<any[]>([])

    // UI State
    const [newMessage, setNewMessage] = useState('')
    const [loadingChannels, setLoadingChannels] = useState(true)
    const [isUploading, setIsUploading] = useState(false)
    const [isFocused, setIsFocused] = useState(false)
    const [showEmojiPicker, setShowEmojiPicker] = useState(false)
    const [showDeptModal, setShowDeptModal] = useState(false)

    // Filters
    const [searchTerm, setSearchTerm] = useState('')
    const [showUnreadOnly, setShowUnreadOnly] = useState(false)

    // Refs
    const messagesEndRef = useRef<HTMLDivElement>(null)
    const fileInputRef = useRef<HTMLInputElement>(null)
    const audioRef = useRef<HTMLAudioElement>(new Audio('/sounds/whats.mp3'))
    const lastMsgCountRef = useRef(0)

    // Admin stuff
    const [activeTab, setActiveTab] = useState<'chats' | 'members'>('chats')
    const [members, setMembers] = useState<any[]>([])
    const [loadingMembers, setLoadingMembers] = useState(false)

    const isAdmin = userRole === 'admin' || userRole === 'owner' || currentOrg?.ownerId === user?.uid

    // Scroll to bottom
    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" })
    }

    // Handle Deeplink
    useEffect(() => {
        const params = new URLSearchParams(location.search)
        const channelId = params.get('channelId')
        if (channelId) {
            setActiveChannelId(channelId)
        }
    }, [location.search])

    useEffect(() => {
        scrollToBottom()

        // Play sound if new message arrived (and not from me)
        if (messages.length > lastMsgCountRef.current && lastMsgCountRef.current > 0) {
            const lastMsg = messages[messages.length - 1]
            if (lastMsg.senderId !== user?.uid) {
                audioRef.current.play().catch(e => console.log('Audio play failed', e))
            }
        }
        lastMsgCountRef.current = messages.length
    }, [messages, user?.uid])

    // Load Channels
    useEffect(() => {
        if (!user || !currentOrg) return

        const q = query(
            collection(db, 'chat_channels'),
            where('orgId', '==', currentOrg.id),
            where('participants', 'array-contains', user.uid),
            orderBy('updatedAt', 'desc')
        )

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const data = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            } as ChatChannel))
            setChannels(data)
            setLoadingChannels(false)
        })

        // Also Load Departments
        const loadDepts = async () => {
            const dq = query(collection(db, 'chat_departments'), where('orgId', '==', currentOrg.id))
            const snap = await getDocs(dq)
            setDepartments(snap.docs.map(d => ({ id: d.id, ...d.data() })))
        }
        loadDepts()

        return () => unsubscribe()
    }, [user, currentOrg])

    // Load Members for Admin
    useEffect(() => {
        if (!isAdmin || !currentOrg || activeTab !== 'members') return

        const fetchMembers = async () => {
            setLoadingMembers(true)
            try {
                const q = query(collection(db, 'members'), where('orgId', '==', currentOrg.id))
                const snapshot = await getDocs(q)
                const membersData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))

                // Fetch user names
                const memberUserIds = membersData.map(m => m.userId)
                if (memberUserIds.length > 0) {
                    const usersPromises = memberUserIds.map(uid => getDoc(doc(db, 'users', uid)))
                    const userSnapshots = await Promise.all(usersPromises)
                    const enrichedMembers = membersData.map(m => {
                        const uSnap = userSnapshots.find(u => u.id === m.userId)
                        return { ...m, name: uSnap?.data()?.displayName || uSnap?.data()?.email, photo: uSnap?.data()?.photoURL }
                    })
                    setMembers(enrichedMembers)
                } else {
                    setMembers(membersData)
                }

            } catch (error) {
                console.error("Error fetching members:", error)
            } finally {
                setLoadingMembers(false)
            }
        }

        fetchMembers()
    }, [isAdmin, currentOrg, activeTab])


    const [userProfiles, setUserProfiles] = useState<Record<string, { name: string, photo: string | null }>>({})

    // Load User Profiles for Live Updates
    useEffect(() => {
        if (channels.length === 0) return

        const script = async () => {
            const uniqueUserIds = new Set<string>()
            channels.forEach(c => {
                c.participants.forEach(uid => {
                    if (uid !== user?.uid) uniqueUserIds.add(uid)
                })
            })

            const ids = Array.from(uniqueUserIds)
            if (ids.length === 0) return

            // We can resolve these IDs. For efficiency in MVP, we'll just fetch them once on load/change.
            // If we want REAL-REAL time, we'd snapshot them. snapshotting 20 users is fine using 'in' query batches.
            // Let's do simple fetch for now, user asked "if it updates", this covers "on refresh/reload of chat list" and "on navigation".
            // To support "on rename immediate reflection in OTHER's chat", we need onSnapshot.

            // Let's implement onSnapshot for the top 10 most recent? No, simple batch fetch first.
            try {
                // Batch by 10
                const batches = []
                for (let i = 0; i < ids.length; i += 10) {
                    batches.push(ids.slice(i, i + 10))
                }

                const newProfiles: Record<string, any> = {}

                await Promise.all(batches.map(async batchIds => {
                    const q = query(collection(db, 'users'), where('uid', 'in', batchIds))
                    const s = await getDocs(q)
                    s.docs.forEach(d => {
                        const data = d.data()
                        newProfiles[d.id] = {
                            name: data.displayName || data.name || data.email,
                            photo: data.photoURL
                        }
                    })
                }))

                setUserProfiles(prev => ({ ...prev, ...newProfiles }))
            } catch (e) {
                console.error("Error syncing profiles", e)
            }
        }
        script()
    }, [channels.length, user?.uid]) // Re-run if channel count changes


    // Load Messages
    useEffect(() => {
        if (!activeChannelId) {
            setMessages([])
            lastMsgCountRef.current = 0
            return
        }

        const q = query(
            collection(db, 'chat_messages'),
            where('channelId', '==', activeChannelId)
        )

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const data = snapshot.docs
                .map(doc => ({
                    id: doc.id,
                    ...doc.data()
                } as ChatMessage))
                .sort((a, b) => {
                    const tA = a.createdAt?.seconds || Date.now() / 1000
                    const tB = b.createdAt?.seconds || Date.now() / 1000
                    return tA - tB
                })

            setMessages(data)
        }, (error) => {
            console.error("Chat Error:", error)
        })

        return () => unsubscribe()
    }, [activeChannelId])


    // Helpers
    const getChannelName = (channel: ChatChannel) => {
        // @ts-ignore
        if (channel.departmentId) {
            const dept = departments.find(d => d.id === channel.departmentId) // @ts-ignore
            return dept ? dept.name : (channel.participantsData?.find(p => p.id === 'dept')?.name || 'Suporte')
        }
        // @ts-ignore
        if (channel.name) return channel.name

        const otherPersonId = channel.participants.find(uid => uid !== user?.uid)
        if (otherPersonId && userProfiles[otherPersonId]) {
            return userProfiles[otherPersonId].name
        }

        const otherPerson = channel.participantsData?.find(p => p.id !== user?.uid)
        return otherPerson?.name || 'Chat'
    }

    const getChannelAvatar = (channel: ChatChannel) => {
        // @ts-ignore
        if (channel.departmentId) return <Briefcase size={16} />

        const otherPersonId = channel.participants.find(uid => uid !== user?.uid)
        if (otherPersonId && userProfiles[otherPersonId]?.photo) {
            return <img src={userProfiles[otherPersonId].photo!} alt="" className="w-full h-full object-cover rounded-full" />
        }
        if (otherPersonId && userProfiles[otherPersonId]) {
            return userProfiles[otherPersonId].name[0]
        }

        const otherPerson = channel.participantsData?.find(p => p.id !== user?.uid)
        return otherPerson?.name?.[0] || '?'
    }



    const handleSendMessage = async (imageUrl?: string) => {
        if ((!newMessage.trim() && !imageUrl) || !activeChannelId || !user) return

        try {
            const messageData = {
                channelId: activeChannelId,
                senderId: user.uid,
                senderName: userData?.displayName || user.displayName || 'Usuário',
                senderPhoto: userData?.photoURL || user.photoURL || null,
                senderRole: isAdmin ? 'admin' : 'member',
                content: newMessage,
                imageUrl: imageUrl || null,
                readBy: [user.uid],
                createdAt: serverTimestamp()
            }

            await addDoc(collection(db, 'chat_messages'), messageData)

            const channelRef = doc(db, 'chat_channels', activeChannelId)
            const channelDoc = await getDoc(channelRef)
            await updateDoc(channelRef, {
                lastMessage: {
                    content: imageUrl ? '📷 Imagem' : newMessage,
                    senderId: user.uid,
                    createdAt: new Date()
                },
                updatedAt: serverTimestamp()
            })

            // Notifications
            if (channelDoc.exists() && currentOrg) {
                const channelData = channelDoc.data() as ChatChannel
                // Filter out self
                const recipients = channelData.participants.filter(uid => uid !== user.uid)
                recipients.forEach(recipientId => {
                    notificationService.create(
                        recipientId,
                        currentOrg.id,
                        'new_message',
                        'Nova mensagem',
                        `${messageData.senderName}: ${messageData.content.substring(0, 30)}...`,
                        `/communication?channelId=${activeChannelId}`
                    )
                })
            }

            setNewMessage('')
            setShowEmojiPicker(false)
        } catch (error) {
            console.error(error)
            toast.error("Erro ao enviar mensagem")
        }
    }

    const handleStartDeptChat = async (dept: any) => {
        if (!user || !currentOrg) return
        setShowDeptModal(false)

        // Check for existing chat with this department
        // We can check the locally loaded 'channels' since they are filtered by user participation
        const existing = channels.find(c => c.departmentId === dept.id)
        if (existing) {
            setActiveChannelId(existing.id)
            return
        }

        // If not, Create New
        let targetParticipants = dept.responsibleMemberIds || []
        if (targetParticipants.length === 0) {
            // Fallback 1: Buscar na coleção 'members'
            const q = query(collection(db, 'members'), where('orgId', '==', currentOrg.id), where('role', 'in', ['admin', 'owner']))
            const s = await getDocs(q)
            targetParticipants = s.docs.map(d => d.data().userId)

            // Fallback 2: Se ainda vazio, buscar na coleção 'users' diretamente (admin global ou owner da org)
            if (targetParticipants.length === 0) {
                // Vamos pegar o owner da org atual
                if (currentOrg.ownerId) {
                    targetParticipants.push(currentOrg.ownerId)
                }
            }
        }

        const participants = Array.from(new Set([user.uid, ...targetParticipants]))

        try {
            const channelData = {
                orgId: currentOrg.id,
                participants,
                participantsData: [
                    { id: user.uid, name: userData?.displayName || 'Usuário', photo: user.photoURL },
                    { id: 'dept', name: dept.name, photo: null } // Abstract name
                ],
                departmentId: dept.id,
                updatedAt: serverTimestamp(),
                createdAt: serverTimestamp()
            }

            const docRef = await addDoc(collection(db, 'chat_channels'), channelData)
            setActiveChannelId(docRef.id)
            toast.success(`Conversa iniciada com ${dept.name}`)
        } catch (e) {
            toast.error('Erro ao iniciar')
        }
    }

    const handleStartChatWithMember = async (member: any) => {
        if (!user || !currentOrg) return
        // Check existing...
        const existing = channels.find(c => c.participants.length === 2 && c.participants.includes(member.userId))
        if (existing) {
            setActiveChannelId(existing.id)
            setActiveTab('chats')
            return
        }

        try {
            const channelData = {
                orgId: currentOrg.id,
                participants: [user.uid, member.userId],
                participantsData: [
                    { id: user.uid, name: userData?.displayName || 'Me', photo: user.photoURL },
                    { id: member.userId, name: member.name || 'Membro', photo: null }
                ],
                updatedAt: serverTimestamp(),
                createdAt: serverTimestamp()
            }
            const docRef = await addDoc(collection(db, 'chat_channels'), channelData)
            setActiveChannelId(docRef.id)
            setActiveTab('chats')
        } catch (e) {
            toast.error('Erro ao criar')
        }
    }

    const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (!file || !activeChannelId || !user) return

        setIsUploading(true)
        try {
            const path = `chat_uploads/${activeChannelId}/${Date.now()}_${file.name}`
            const storageRef = ref(storage, path)
            const snapshot = await uploadBytes(storageRef, file)
            const downloadUrl = await getDownloadURL(snapshot.ref)
            await handleSendMessage(downloadUrl)
            toast.success("Imagem enviada!")
        } catch (error) {
            toast.error("Erro ao enviar imagem")
        } finally {
            setIsUploading(false)
            if (fileInputRef.current) fileInputRef.current.value = ''
        }
    }

    const handleDeleteChannel = async () => {
        if (!activeChannelId) return
        if (!confirm("Tem certeza que deseja apagar esta conversa? O histórico será perdido para todos.")) return

        try {
            await deleteDoc(doc(db, 'chat_channels', activeChannelId))
            // Optionally delete messages (cloud function better for this)
            setActiveChannelId(null)
            toast.success('Conversa apagada')
        } catch (e) {
            toast.error('Erro ao apagar')
        }
    }



    // Filter channels
    const filteredChannels = channels.filter(c => {
        const matchesSearch = getChannelName(c).toLowerCase().includes(searchTerm.toLowerCase()) ||
            c.lastMessage?.content.toLowerCase().includes(searchTerm.toLowerCase())
        const matchesUnread = showUnreadOnly ? (c.unreadCount && c.unreadCount > 0) : true
        return matchesSearch && matchesUnread
    })

    return (
        <div className="bg-white rounded-[2rem] border border-[#001c28]/5 shadow-xl shadow-[#001c28]/5 overflow-hidden flex h-[600px]">
            {/* Sidebar */}
            <div className={`w-80 border-r border-[#001c28]/5 bg-[#f8f9fa] flex flex-col ${activeChannelId ? 'hidden md:flex' : 'flex'}`}>
                <div className="p-4 border-b border-[#001c28]/5 space-y-3">

                    {isAdmin && (
                        <div className="flex p-1 bg-gray-200 rounded-lg mb-2">
                            <button
                                onClick={() => setActiveTab('chats')}
                                className={`flex-1 py-1.5 text-xs font-bold rounded-md transition-all ${activeTab === 'chats' ? 'bg-white shadow text-[#001c28]' : 'text-gray-500'}`}
                            >
                                Conversas
                            </button>
                            <button
                                onClick={() => setActiveTab('members')}
                                className={`flex-1 py-1.5 text-xs font-bold rounded-md transition-all ${activeTab === 'members' ? 'bg-white shadow text-[#001c28]' : 'text-gray-500'}`}
                            >
                                Membros
                            </button>
                        </div>
                    )}

                    {!isAdmin && (
                        <button
                            onClick={() => setShowDeptModal(true)}
                            className="w-full py-2.5 bg-[#d4a94d] hover:bg-[#b08d4ae0] text-[#001c28] text-xs font-black uppercase tracking-widest rounded-xl shadow-md transition-all flex items-center justify-center gap-2"
                        >
                            <MessageCircle size={16} />
                            Abrir Chat
                        </button>
                    )}

                    <div className="relative">
                        <Search className="absolute left-3 top-2.5 text-gray-400" size={16} />
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Buscar conversa..."
                            className="w-full bg-white border border-[#001c28]/5 rounded-xl py-2 pl-9 pr-4 text-sm font-medium focus:outline-none focus:border-[#d4a94d]"
                        />
                    </div>

                    <button
                        onClick={() => setShowUnreadOnly(!showUnreadOnly)}
                        className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-xs font-bold border transition-all ${showUnreadOnly ? 'bg-[#d4a94d]/10 border-[#d4a94d] text-[#001c28]' : 'bg-white border-transparent text-gray-500 hover:bg-gray-200'
                            }`}
                    >
                        <span className="flex items-center gap-2"><Filter size={14} /> Apenas não lidas</span>
                        {showUnreadOnly && <Check size={14} className="text-[#d4a94d]" />}
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto">
                    {activeTab === 'chats' ? (
                        <>
                            {loadingChannels ? (
                                <div className="p-4 text-center text-gray-400 text-xs">Carregando conversas...</div>
                            ) : filteredChannels.length === 0 ? (
                                <div className="p-8 text-center">
                                    <p className="text-gray-400 text-sm mb-4">Nenhuma conversa encontrada.</p>
                                </div>
                            ) : (
                                filteredChannels.map(channel => (
                                    <button
                                        key={channel.id}
                                        onClick={() => setActiveChannelId(channel.id)}
                                        className={`w-full p-4 flex items-center gap-3 hover:bg-white transition-colors border-b border-[#001c28]/5 text-left ${activeChannelId === channel.id ? 'bg-white border-l-4 border-l-[#d4a94d]' : 'border-l-4 border-l-transparent'}`}
                                    >
                                        <div className="w-10 h-10 rounded-full bg-[#001c28] flex items-center justify-center text-white font-bold text-xs uppercase">
                                            {getChannelAvatar(channel)}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex justify-between items-baseline mb-1">
                                                <span className={`text-sm font-bold truncate ${activeChannelId === channel.id ? 'text-[#001c28]' : 'text-gray-600'}`}>
                                                    {getChannelName(channel)}
                                                </span>
                                                {/* @ts-ignore */}
                                                <span className="text-[9px] text-gray-400">{channel.updatedAt?.toDate?.().toLocaleDateString()}</span>
                                            </div>
                                            <p className="text-xs text-gray-500 truncate font-medium">
                                                {channel.lastMessage?.content || 'Inicie a conversa...'}
                                            </p>
                                        </div>
                                        {channel.unreadCount && channel.unreadCount > 0 ? (
                                            <span className="bg-[#d4a94d] text-[#001c28] text-[10px] font-black h-5 w-5 rounded-full flex items-center justify-center">
                                                {channel.unreadCount}
                                            </span>
                                        ) : null}
                                    </button>
                                ))
                            )}
                        </>
                    ) : (
                        // Members List
                        <div className="divide-y divide-gray-100">
                            {members.map(member => (
                                <button
                                    key={member.id}
                                    onClick={() => handleStartChatWithMember(member)}
                                    className="w-full p-4 flex items-center gap-3 hover:bg-white transition-colors text-left"
                                >
                                    <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 font-bold text-xs">
                                        {member.name?.[0] || '?'}
                                    </div>
                                    <div className="flex-1">
                                        <p className="text-sm font-bold text-gray-700">{member.name || 'Membro'}</p>
                                        <p className="text-xs text-gray-400">{member.role === 'admin' ? 'Administrador' : 'Membro'}</p>
                                    </div>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Chat Area */}
            {activeChannelId ? (
                <div className={`flex-1 flex flex-col bg-white ${activeChannelId ? 'flex' : 'hidden md:flex'}`}>
                    {/* Chat Header */}
                    <div className="p-4 border-b border-[#001c28]/5 flex justify-between items-center bg-white z-10">
                        <div className="flex items-center gap-3">
                            {/* Mobile Back Button */}
                            <button onClick={() => setActiveChannelId(null)} className="md:hidden p-2 text-gray-500"><Search size={16} className="rotate-180" /></button> {/* Using search icon as back arrow for reuse or import ArrowLeft */}

                            <div className="w-10 h-10 rounded-full bg-[#001c28] flex items-center justify-center text-white font-bold text-xs uppercase">
                                {channels.find(c => c.id === activeChannelId) && getChannelAvatar(channels.find(c => c.id === activeChannelId)!)}
                            </div>
                            <div>
                                <h3 className="font-bold text-[#001c28]">
                                    {channels.find(c => c.id === activeChannelId) && getChannelName(channels.find(c => c.id === activeChannelId)!)}
                                </h3>
                                <p className="text-[10px] text-green-500 font-bold uppercase tracking-wider flex items-center gap-1">
                                    <span className="w-2 h-2 rounded-full bg-green-500"></span> Online
                                </p>
                            </div>
                        </div>

                        <button onClick={handleDeleteChannel} className="p-2 hover:bg-red-50 text-gray-400 hover:text-red-500 rounded-lg transition-colors" title="Apagar conversa">
                            <Trash2 size={20} />
                        </button>
                    </div>

                    {/* Messages */}
                    <div className="flex-1 overflow-y-auto p-6 space-y-6 bg-slate-50/50">
                        {messages.length === 0 && (
                            <div className="flex items-center justify-center h-full text-gray-400 text-sm italic">
                                Nenhuma mensagem ainda. Diga olá!
                            </div>
                        )}
                        {messages.map(msg => {
                            const isMe = msg.senderId === user?.uid;
                            // @ts-ignore
                            const isAdminMsg = msg.senderRole === 'owner' || msg.senderRole === 'admin';

                            return (
                                <div key={msg.id} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                                    <div className={`max-w-[70%] flex flex-col ${isMe ? 'items-end' : 'items-start'}`}>

                                        {!isMe && (
                                            <div className="flex items-center gap-2 mb-1 pl-1">
                                                <span className="text-[10px] font-bold text-gray-500">{msg.senderName}</span>
                                                {isAdminMsg && (
                                                    <span className="bg-[#d4a94d] text-[#001c28] text-[9px] font-black px-1.5 py-0.5 rounded-md uppercase tracking-wider">
                                                        Admin
                                                    </span>
                                                )}
                                            </div>
                                        )}

                                        <div className={`p-4 rounded-2xl shadow-sm text-sm font-medium leading-relaxed relative group ${isMe
                                            ? 'bg-[#001c28] text-white rounded-tr-none'
                                            : isAdminMsg
                                                ? 'bg-white border-l-4 border-l-[#d4a94d] text-gray-800 rounded-tl-none' // Admin message style
                                                : 'bg-white text-gray-700 border border-gray-100 rounded-tl-none'
                                            }`}>
                                            {msg.imageUrl && (
                                                <div className="mb-3 rounded-lg overflow-hidden">
                                                    <img src={msg.imageUrl} alt="Anexo" className="max-w-full h-auto object-cover max-h-60 rounded-md" />
                                                </div>
                                            )}
                                            <p>{msg.content}</p>
                                        </div>
                                        <div className="text-[9px] text-gray-400 mt-1 px-1 opacity-70 group-hover:opacity-100 transition-opacity">
                                            {msg.createdAt?.toDate().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                            {isMe && <span className="ml-1">✓</span>}
                                        </div>
                                    </div>
                                </div>
                            )
                        })}
                        <div ref={messagesEndRef} />
                    </div>

                    {/* Input Area */}
                    <div className="p-6 bg-white border-t border-[#001c28]/5 z-20 relative">
                        {showEmojiPicker && (
                            <div className="absolute bottom-24 left-6 z-50 shadow-2xl rounded-2xl bg-white p-2 border border-gray-100">
                                <div className="flex justify-end mb-1">
                                    <button onClick={() => setShowEmojiPicker(false)} className="p-1 hover:bg-gray-100 rounded-full text-gray-400">
                                        <X size={14} />
                                    </button>
                                </div>
                                <EmojiPicker onEmojiClick={(e) => setNewMessage(p => p + e.emoji)} height={350} />
                            </div>
                        )}

                        {isUploading && (
                            <div className="text-xs text-[#d4a94d] mb-2 font-bold animate-pulse flex items-center gap-2">
                                <Loader2 size={12} className="animate-spin" />
                                Enviando imagem...
                            </div>
                        )}
                        <form
                            onSubmit={(e) => {
                                e.preventDefault()
                                handleSendMessage()
                            }}
                            className="bg-[#f8f9fa] p-3 rounded-2xl border border-[#001c28]/5 focus-within:border-[#d4a94d]/50 focus-within:ring-2 focus-within:ring-[#d4a94d]/10 transition-all shadow-sm"
                        >
                            <textarea
                                value={newMessage}
                                onChange={(e) => setNewMessage(e.target.value)}
                                onFocus={() => setIsFocused(true)}
                                onBlur={() => setIsFocused(false)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && !e.shiftKey) {
                                        e.preventDefault()
                                        handleSendMessage()
                                    }
                                }}
                                placeholder="Digite sua mensagem..."
                                className="w-full bg-transparent border-none focus:ring-0 text-sm font-medium text-[#001c28] placeholder:text-gray-400 min-h-[60px] resize-none"
                            />

                            <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-100">
                                <div className="flex items-center gap-2">
                                    <button
                                        type="button"
                                        onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                                        className="p-2 text-gray-400 hover:text-[#d4a94d] hover:bg-yellow-50 rounded-xl transition-colors"
                                    >
                                        <Smile size={18} />
                                    </button>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        className="hidden"
                                        ref={fileInputRef}
                                        onChange={handleFileUpload}
                                    />
                                    <button
                                        type="button"
                                        disabled={isUploading}
                                        onClick={() => fileInputRef.current?.click()}
                                        className="p-2 text-gray-400 hover:text-[#001c28] hover:bg-gray-200 rounded-xl transition-colors"
                                        title="Anexar Imagem"
                                    >
                                        <Paperclip size={18} />
                                    </button>
                                </div>
                                <button
                                    type="submit"
                                    disabled={(!newMessage.trim() && !isUploading)}
                                    className={`px-6 py-2 rounded-xl transition-all shadow-sm flex items-center gap-2 text-xs font-black uppercase tracking-widest ${!newMessage.trim() && !isUploading
                                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                        : 'bg-[#001c28] text-[#d4a94d] hover:shadow-md hover:scale-105'
                                        }`}
                                >
                                    <span>Enviar</span>
                                    <Send size={14} fill="currentColor" className={isFocused ? 'animate-pulse' : ''} />
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            ) : (
                <div className="hidden md:flex flex-1 flex-col items-center justify-center text-gray-300 bg-white">
                    <div className="w-20 h-20 bg-[#f8f9fa] rounded-full flex items-center justify-center mb-4">
                        <MessageSquare size={40} className="text-[#001c28]/20" />
                    </div>
                    <p className="font-bold text-[#001c28]/40">Selecione uma conversa</p>
                </div>
            )}

            {/* Dept Modal */}
            {showDeptModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#001c28]/80 backdrop-blur-sm animate-in fade-in duration-200">
                    <div className="bg-white rounded-[2rem] w-full max-w-lg p-8 shadow-2xl relative">
                        <button onClick={() => setShowDeptModal(false)} className="absolute top-6 right-6 p-2 bg-gray-100 rounded-full text-gray-400 hover:bg-gray-200">
                            <X size={16} />
                        </button>
                        <h3 className="text-xl font-black text-[#001c28] uppercase italic mb-6">Abrir Chat com:</h3>
                        <p className="text-sm font-bold text-gray-500 mb-4">Selecione o departamento:</p>

                        <div className="space-y-3">
                            {departments.length === 0 ? (
                                <div className="text-center py-4">
                                    <p className="text-sm text-gray-400">Sem departamentos específicos.</p>
                                    <button
                                        onClick={() => handleStartDeptChat({ id: 'general', name: 'Suporte Geral', responsibleMemberIds: [] })}
                                        className="mt-2 text-[#d4a94d] font-bold underline"
                                    >
                                        Falar com Admin Geral
                                    </button>
                                </div>
                            ) : departments.map(dept => (
                                <button
                                    key={dept.id}
                                    onClick={() => handleStartDeptChat(dept)}
                                    className="w-full flex items-center gap-4 p-4 rounded-xl border border-[#001c28]/10 hover:border-[#d4a94d] hover:bg-[#d4a94d]/5 transition-all text-left group"
                                >
                                    <div className="w-10 h-10 bg-[#001c28] rounded-full flex items-center justify-center text-white group-hover:bg-[#d4a94d] transition-colors">
                                        <Briefcase size={18} />
                                    </div>
                                    <div>
                                        <h4 className="font-bold text-[#001c28] group-hover:text-[#d4a94d] transition-colors">{dept.name}</h4>
                                        <p className="text-xs text-gray-400">Iniciar atendimento</p>
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}
